import { META_UNILEVER_COLUMNS, MetaUnileverRow, SkippedRow, ValidationError } from './types';

export const EXPECTED_HEADERS = META_UNILEVER_COLUMNS.map((column) => column.label);
export const REQUIRED_IMPORT_HEADERS = ['Distribuidor', 'Consultor', 'Coordenador', 'Executivo BPC', 'Executivo HF', 'Regional', 'Meta HPF'];
export const OPTIONAL_IMPORT_HEADERS = ['Mês', 'Ano'];

const MONTH_LABELS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export const MONTH_OPTIONS = MONTH_LABELS.map((label, index) => ({
  value: String(index + 1),
  label,
}));

const MONTH_ALIASES = new Map<string, number>([
  ['jan', 1],
  ['janeiro', 1],
  ['fev', 2],
  ['fevereiro', 2],
  ['mar', 3],
  ['marco', 3],
  ['março', 3],
  ['abr', 4],
  ['abril', 4],
  ['mai', 5],
  ['maio', 5],
  ['jun', 6],
  ['junho', 6],
  ['jul', 7],
  ['julho', 7],
  ['ago', 8],
  ['agosto', 8],
  ['set', 9],
  ['setembro', 9],
  ['out', 10],
  ['outubro', 10],
  ['nov', 11],
  ['novembro', 11],
  ['dez', 12],
  ['dezembro', 12],
]);

export function makeLocalGuid() {
  return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createEmptyRow(): MetaUnileverRow {
  return {
    guid: makeLocalGuid(),
    distribuidor: '',
    consultor: '',
    coordenador: '',
    executivoBpc: '',
    executivoHf: '',
    regional: '',
    mes: '',
    ano: '',
    metaHpf: '',
  };
}

export function normalizeText(value: any) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

export function normalizeKey(value: any) {
  return normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export function formatMeta(value: any) {
  if (value === null || value === undefined || value === '') return '';
  const numberValue = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(numberValue)) return String(value);
  return numberValue.toFixed(2);
}

export function parseMetaNumber(value: any) {
  const formatted = formatMeta(value);
  if (formatted === '') return null;
  const numberValue = Number(formatted);
  return Number.isFinite(numberValue) ? numberValue : null;
}

export function formatCurrencyBRL(value: any) {
  const numberValue = parseMetaNumber(value);
  if (numberValue === null) return '';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numberValue);
}

export function sanitizeMetaInput(value: string) {
  return value.replace(',', '.').replace(/[^\d.-]/g, '');
}

export function finalizeMetaValue(value: any) {
  const formatted = formatMeta(value);
  return formatted === '' ? '' : formatted;
}

export function parseMonthNumber(value: any) {
  const text = normalizeText(value);
  if (!text) return null;

  const numericValue = Number(text.replace(',', '.'));
  if (Number.isInteger(numericValue) && numericValue >= 1 && numericValue <= 12) {
    return numericValue;
  }

  const month = MONTH_ALIASES.get(normalizeKey(text));
  return month || null;
}

export function finalizeMonthValue(value: any) {
  const month = parseMonthNumber(value);
  return month === null ? normalizeText(value) : String(month);
}

export function formatMonthLabel(value: any) {
  const month = parseMonthNumber(value);
  return month === null ? normalizeText(value) : MONTH_LABELS[month - 1];
}

export function getMonthSelectValue(value: any) {
  const month = parseMonthNumber(value);
  return month === null ? '' : String(month);
}

export function parseYearNumber(value: any) {
  const text = normalizeText(value);
  if (!text) return null;
  const year = Number(text);
  if (!Number.isInteger(year) || year < 1900 || year > 2200) return null;
  return year;
}

export function finalizeYearValue(value: any) {
  const year = parseYearNumber(value);
  return year === null ? normalizeText(value) : String(year);
}

export function sanitizeYearInput(value: string) {
  return value.replace(/[^\d]/g, '').slice(0, 4);
}

export function getYearSelectValue(value: any) {
  const year = parseYearNumber(value);
  return year === null ? '' : String(year);
}

export function getYearOptions(baseYear = new Date().getFullYear()) {
  return Array.from({ length: 6 }, (_, index) => baseYear - index);
}

export function prepareRowsForSave(rows: MetaUnileverRow[]) {
  return rows
    .map((row) => ({
      ...row,
      distribuidor: normalizeText(row.distribuidor),
      consultor: normalizeText(row.consultor),
      coordenador: normalizeText(row.coordenador),
      executivoBpc: normalizeText(row.executivoBpc),
      executivoHf: normalizeText(row.executivoHf),
      regional: normalizeText(row.regional),
      mes: finalizeMonthValue(row.mes) || null,
      ano: finalizeYearValue(row.ano) || null,
      metaHpf: formatMeta(row.metaHpf) || null,
    }))
    .filter((row) => !isBlankRow(row));
}

export function isBlankRow(row: MetaUnileverRow) {
  return META_UNILEVER_COLUMNS.every((column) => {
    const value = row[column.key];
    return value === null || value === undefined || String(value).trim() === '';
  });
}

export function rowsAreEqual(a: MetaUnileverRow, b: MetaUnileverRow) {
  return META_UNILEVER_COLUMNS.every((column) => {
    const left = column.type === 'money' ? formatMeta(a[column.key]) : normalizeText(a[column.key]);
    const right = column.type === 'money' ? formatMeta(b[column.key]) : normalizeText(b[column.key]);
    return left === right;
  });
}

export function getChangedRows(rows: MetaUnileverRow[], originalRows: MetaUnileverRow[]) {
  const originalByGuid = new Map(originalRows.map((row) => [row.guid, row]));

  return rows.filter((row) => {
    if (isBlankRow(row)) return false;
    const original = originalByGuid.get(row.guid);
    return !original || !rowsAreEqual(row, original);
  });
}

export function mergeRowsByDistributor(
  currentRows: MetaUnileverRow[],
  importedRows: MetaUnileverRow[]
) {
  const nextRows = [...currentRows];
  const indexByDistributor = new Map<string, number>();

  nextRows.forEach((row, index) => {
    if (row.distribuidor) {
      indexByDistributor.set(getRowIdentityKey(row), index);
    }
  });

  importedRows.forEach((row) => {
    const key = getRowIdentityKey(row);
    const index = indexByDistributor.get(key);

    if (index === undefined) {
      nextRows.push(row);
      indexByDistributor.set(key, nextRows.length - 1);
    } else {
      nextRows[index] = {
        ...row,
        guid: nextRows[index].guid,
      };
    }
  });

  return nextRows;
}

export function getRowIdentityKey(row: MetaUnileverRow) {
  return [
    normalizeKey(row.distribuidor),
    parseMonthNumber(row.mes) || '',
    parseYearNumber(row.ano) || '',
  ].join('|');
}

export function validationErrorLines(errors: ValidationError[] = []) {
  return errors.map((error) => {
    const row = error.row > 0 ? `Linha ${error.row}` : 'Tabela';
    return `${row}: ${error.message}`;
  });
}

export function skippedRowLines(rows: SkippedRow[] = []) {
  return rows.map((row) => {
    const reasons = (row.reasons || []).map((reason) => reason.message).join(' | ');
    return `Linha ${row.row}: ${row.distribuidor || '-'} / ${row.regional || '-'}${reasons ? ` - ${reasons}` : ''}`;
  });
}
